import { Box, Chip, Tooltip, Typography } from "@mui/material"
import { InfoRounded } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import { type FunctionComponent } from "react"

import type { Product } from "@/graphql/codegen/graphql"
import { TooltipLink } from "@/screens/Products/components/Product/Information/TechnicalInformation/TechnicalInformationContent"
import { CAS_TOOLTIP_URL, UN_NUMBER_TOOLTIP_URL } from "@/screens/Products/constants"

interface ColumnContentProps {
  content: string | string[]
}

const ColumnContent: FunctionComponent<ColumnContentProps> = (props: ColumnContentProps) => {
  const { content } = props

  return (
    <Box className='mt-0.5 grid w-[190px] grid-cols-2 gap-x-4 text-gray-700'>
      {Array.isArray(content)
        ? content.map((item, i) => (
            <span key={item} className='whitespace-nowrap'>
              {item}
              {i < content.length - 1 && ";"}
            </span>
          ))
        : (content ?? "-")}
    </Box>
  )
}

export const useGetRegulatoryColumns = (): ColumnDef<
  Pick<Product, "casRegistryIds" | "unNumber" | "isRohsCompliant" | "isReachSvhcCompliant">
>[] => {
  return [
    {
      accessorKey: "casRegistryIds",
      header: () => (
        <>
          CAS
          <Tooltip
            title={
              <Typography className='m-1 w-[155px] text-xs font-medium text-white'>
                Search CAS registry number® (cas rn®)
                <TooltipLink href={CAS_TOOLTIP_URL} text='HERE' />
              </Typography>
            }
          >
            <InfoRounded />
          </Tooltip>
        </>
      ),
      cell: ({ getValue }) => <ColumnContent content={getValue<string[]>()} />,
    },
    {
      accessorKey: "unNumber",
      header: () => (
        <>
          UN Number
          <Tooltip
            title={
              <Typography className='m-1 w-[155px] text-xs font-medium text-white'>
                Determine whether your goods qualify for USMCA
                <TooltipLink href={UN_NUMBER_TOOLTIP_URL} text='HERE' />
              </Typography>
            }
          >
            <InfoRounded />
          </Tooltip>
        </>
      ),
      cell: ({ getValue }) => <ColumnContent content={getValue<string>()} />,
    },
    {
      accessorKey: "isRohsCompliant",
      header: "Compliances",
      cell: ({ row }) => {
        return (
          <Box className='flex gap-2'>
            {row.original?.isRohsCompliant || row.original?.isReachSvhcCompliant
              ? [
                  {
                    label: "RoHS Compliant",
                    value: row.original?.isRohsCompliant,
                  },
                  {
                    label: "REACH SVHC Compliant",
                    value: row.original?.isReachSvhcCompliant,
                  },
                ].map(({ label, value }, index) => {
                  return value && <Chip key={`${label}-${index}`} label={label} className='rounded-sm' />
                })
              : "-"}
          </Box>
        )
      },
    },
  ]
}
